<script setup>
import { computed, watch, onMounted, ref } from 'vue';
import {
  useMapGetter,
  useFunctionGetter,
  useStore,
} from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import ContactConversations from './ContactConversations.vue';
import ConversationAction from './ConversationAction.vue';
import ConversationParticipant from './ConversationParticipant.vue';
import ContactInfo from './contact/ContactInfo.vue';
import ContactNotes from './contact/ContactNotes.vue';
import ConversationInfo from './ConversationInfo.vue';
import CustomAttributes from './customAttributes/CustomAttributes.vue';
import SharedFiles from './SharedFiles.vue';
import Draggable from 'vuedraggable';
import MacrosList from './Macros/List.vue';
import GoldmanVpnPanel from 'dashboard/components/widgets/GoldmanVpnPanel.vue';
import ShopifyOrdersList from 'dashboard/components/widgets/conversation/ShopifyOrdersList.vue';
import SidebarActionsHeader from 'dashboard/components-next/SidebarActionsHeader.vue';
import LinearIssuesList from 'dashboard/components/widgets/conversation/linear/IssuesList.vue';
import LinearSetupCTA from 'dashboard/components/widgets/conversation/linear/LinearSetupCTA.vue';

const firstPresent = (...values) =>
  values.find(value => value !== undefined && value !== null && value !== '');

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  inboxId: {
    type: Number,
    default: undefined,
  },
});

const {
  updateUISettings,
  isContactSidebarItemOpen,
  conversationSidebarItemsOrder,
  toggleSidebarUIState,
} = useUISettings();

const dragging = ref(false);
const conversationSidebarItems = ref([]);

const shopifyIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'shopify'
);

const isShopifyFeatureEnabled = computed(
  () => shopifyIntegration.value.enabled
);

const { isCloudFeatureEnabled } = useAccount();

const isLinearFeatureEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.LINEAR)
);

const linearIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'linear'
);

const isLinearClientIdConfigured = computed(() => {
  return !!linearIntegration.value?.id;
});

const isLinearConnected = computed(
  () => linearIntegration.value?.enabled || false
);

const store = useStore();
const currentChat = useMapGetter('getSelectedChat');
const conversationId = computed(() => props.conversationId);
const currentInboxId = computed(
  () => props.inboxId || currentChat.value?.inbox_id
);
const currentInbox = useFunctionGetter('inboxes/getInbox', currentInboxId);
const conversationMetadataGetter = useMapGetter(
  'conversationMetadata/getConversationMetadata'
);
const currentConversationMetaData = computed(() =>
  conversationMetadataGetter.value(conversationId.value)
);
const conversationAdditionalAttributes = computed(
  () => currentConversationMetaData.value.additional_attributes || {}
);

const channelType = computed(() => currentChat.value.meta?.channel);
const activeAgentBot = useFunctionGetter(
  'agentBots/getActiveAgentBot',
  currentInboxId
);
const currentInboxName = computed(() => currentInbox.value?.name || '');

const contactGetter = useMapGetter('contacts/getContact');
const contactId = computed(() => currentChat.value.meta?.sender?.id);
const contact = computed(() => contactGetter.value(contactId.value));
const contactAdditionalAttributes = computed(
  () => contact.value?.additional_attributes || {}
);
const contactCustomAttributes = computed(
  () => contact.value?.custom_attributes || {}
);
const senderIdentifier = computed(
  () =>
    currentChat.value.meta?.sender?.identifier ||
    contact.value?.identifier ||
    ''
);
const normalizedTelegramId = computed(() => {
  const candidate = firstPresent(
    contactAdditionalAttributes.value.social_telegram_user_id,
    contactAdditionalAttributes.value.telegram_id,
    contactCustomAttributes.value.telegram_id,
    contactCustomAttributes.value.telegramId,
    senderIdentifier.value.match(/^\d+$/) ? senderIdentifier.value : null
  );

  return candidate ? String(candidate) : '';
});
const normalizedTelegramUsername = computed(
  () =>
    firstPresent(
      contactAdditionalAttributes.value.social_telegram_user_name,
      contactAdditionalAttributes.value.username,
      contactCustomAttributes.value.telegram_username,
      contactCustomAttributes.value.telegramUsername
    ) || ''
);
const serviceOrigin = computed(
  () =>
    conversationAdditionalAttributes.value.service_origin ||
    contactAdditionalAttributes.value.service_origin ||
    contactCustomAttributes.value.service_origin ||
    null
);
const isGoldmanConversation = computed(() => {
  const activeBotName = activeAgentBot.value?.name || '';
  const isGoldmanBotInbox = ['Goldman AI', 'Goldman VPS AI'].includes(
    activeBotName
  );
  const isGoldmanInbox = currentInboxName.value
    .toLowerCase()
    .includes('goldman');
  const isGoldmanSiteConversation =
    serviceOrigin.value === 'goldmanvps_org' ||
    senderIdentifier.value.startsWith('goldmanvps:user:') ||
    senderIdentifier.value.startsWith('goldmanvps:visitor:');

  return isGoldmanBotInbox || isGoldmanInbox || isGoldmanSiteConversation;
});
const isHobbyConversation = computed(() => {
  const activeBotName = activeAgentBot.value?.name || '';
  const isHobbyBotInbox = ['Hobby AI', 'Hobby VPN AI'].includes(activeBotName);
  const isHobbyInbox = currentInboxName.value.toLowerCase().includes('hobby');
  const isHobbySiteConversation =
    serviceOrigin.value === 'hobbyvps_online' ||
    senderIdentifier.value.startsWith('hobbyvps:user:') ||
    senderIdentifier.value.startsWith('hobbyvps:visitor:');

  return isHobbyBotInbox || isHobbyInbox || isHobbySiteConversation;
});
const usesLegacyGoldmanContactInfo = computed(
  () =>
    (isGoldmanConversation.value || isHobbyConversation.value) &&
    Boolean(normalizedTelegramId.value || normalizedTelegramUsername.value)
);
const contactInfoContact = computed(() => {
  if (!contact.value || !usesLegacyGoldmanContactInfo.value) {
    return contact.value;
  }

  return {
    ...contact.value,
    additional_attributes: {
      ...(contact.value.additional_attributes || {}),
      ...(normalizedTelegramUsername.value
        ? { social_telegram_user_name: normalizedTelegramUsername.value }
        : {}),
      ...(normalizedTelegramId.value
        ? { social_telegram_user_id: normalizedTelegramId.value }
        : {}),
    },
  };
});

const getContactDetails = () => {
  if (contactId.value) {
    store.dispatch('contacts/show', { id: contactId.value });
  }
};

watch(contactId, (newContactId, prevContactId) => {
  if (newContactId && newContactId !== prevContactId) {
    getContactDetails();
  }
});

const onDragEnd = () => {
  dragging.value = false;
  updateUISettings({
    conversation_sidebar_items_order: conversationSidebarItems.value,
  });
};

const closeContactPanel = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
  });
};

onMounted(() => {
  conversationSidebarItems.value = conversationSidebarItemsOrder.value;
  getContactDetails();
  store.dispatch('attributes/get', 0);
  store.dispatch('inboxes/get');
  store.dispatch('agentBots/get');
  // Load integrations to ensure linear integration state is available
  store.dispatch('integrations/get', 'linear');
});

watch(
  currentInboxId,
  inboxId => {
    if (inboxId) {
      store.dispatch('agentBots/fetchAgentBotInbox', inboxId);
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="w-full">
    <SidebarActionsHeader
      :title="$t('CONVERSATION.SIDEBAR.CONTACT')"
      @close="closeContactPanel"
    />
    <ContactInfo
      :contact="contactInfoContact"
      :channel-type="channelType"
      :use-legacy-goldman-layout="usesLegacyGoldmanContactInfo"
    />
    <GoldmanVpnPanel
      v-if="(isGoldmanConversation || isHobbyConversation) && contact?.id"
      :conversation="currentChat"
      :contact="contact"
    />
    <div class="px-2 pb-8 list-group">
      <Draggable
        :list="conversationSidebarItems"
        animation="200"
        ghost-class="ghost"
        handle=".drag-handle"
        item-key="name"
        class="flex flex-col gap-3"
        @start="dragging = true"
        @end="onDragEnd"
      >
        <template #item="{ element }">
          <div
            v-if="element.name === 'conversation_actions'"
            class="conversation--actions"
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_ACTIONS')"
              :is-open="isContactSidebarItemOpen('is_conv_actions_open')"
              @toggle="
                value => toggleSidebarUIState('is_conv_actions_open', value)
              "
            >
              <ConversationAction
                :conversation-id="conversationId"
                :inbox-id="inboxId"
              />
            </AccordionItem>
          </div>
          <div
            v-else-if="element.name === 'conversation_participants'"
            class="conversation--actions"
          >
            <AccordionItem
              :title="$t('CONVERSATION_PARTICIPANTS.SIDEBAR_TITLE')"
              :is-open="isContactSidebarItemOpen('is_conv_participants_open')"
              @toggle="
                value =>
                  toggleSidebarUIState('is_conv_participants_open', value)
              "
            >
              <ConversationParticipant
                :conversation-id="conversationId"
                :inbox-id="inboxId"
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'conversation_info'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_INFO')"
              :is-open="isContactSidebarItemOpen('is_conv_details_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_conv_details_open', value)
              "
            >
              <ConversationInfo
                :conversation-attributes="conversationAdditionalAttributes"
                :contact-attributes="contactAdditionalAttributes"
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'contact_attributes'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_ATTRIBUTES')"
              :is-open="isContactSidebarItemOpen('is_contact_attributes_open')"
              compact
              @toggle="
                value =>
                  toggleSidebarUIState('is_contact_attributes_open', value)
              "
            >
              <CustomAttributes
                attribute-type="contact_attribute"
                attribute-from="conversation_contact_panel"
                :contact-id="contact.id"
                :empty-state-message="
                  $t('CONVERSATION_CUSTOM_ATTRIBUTES.NO_RECORDS_FOUND')
                "
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'previous_conversation'">
            <AccordionItem
              v-if="contact.id"
              :title="
                $t('CONVERSATION_SIDEBAR.ACCORDION.PREVIOUS_CONVERSATION')
              "
              :is-open="isContactSidebarItemOpen('is_previous_conv_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_previous_conv_open', value)
              "
            >
              <ContactConversations
                :contact-id="contact.id"
                :conversation-id="conversationId"
              />
            </AccordionItem>
          </div>
          <woot-feature-toggle
            v-else-if="element.name === 'macros'"
            feature-key="macros"
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.MACROS')"
              :is-open="isContactSidebarItemOpen('is_macro_open')"
              compact
              @toggle="value => toggleSidebarUIState('is_macro_open', value)"
            >
              <MacrosList :conversation-id="conversationId" />
            </AccordionItem>
          </woot-feature-toggle>
          <div
            v-else-if="
              element.name === 'linear_issues' &&
              isLinearFeatureEnabled &&
              isLinearClientIdConfigured
            "
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.LINEAR_ISSUES')"
              :is-open="isContactSidebarItemOpen('is_linear_issues_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_linear_issues_open', value)
              "
            >
              <LinearSetupCTA v-if="!isLinearConnected" />
              <LinearIssuesList v-else :conversation-id="conversationId" />
            </AccordionItem>
          </div>
          <div
            v-else-if="
              element.name === 'shopify_orders' && isShopifyFeatureEnabled
            "
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.SHOPIFY_ORDERS')"
              :is-open="isContactSidebarItemOpen('is_shopify_orders_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_shopify_orders_open', value)
              "
            >
              <ShopifyOrdersList :contact-id="contactId" />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'contact_notes'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_NOTES')"
              :is-open="isContactSidebarItemOpen('is_contact_notes_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_contact_notes_open', value)
              "
            >
              <ContactNotes :contact-id="contactId" />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'shared_files'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.SHARED_FILES')"
              :is-open="isContactSidebarItemOpen('is_shared_files_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_shared_files_open', value)
              "
            >
              <SharedFiles />
            </AccordionItem>
          </div>
        </template>
      </Draggable>
    </div>
  </div>
</template>

<style lang="scss" scoped>
:deep(.contact--profile) {
  @apply pb-3 border-b border-solid border-n-weak;
}
</style>
